import { useInfiniteQuery } from '@tanstack/react-query';
import { lireLHistorique } from '@groupe-trousseau/shared/api-client';
import { deballer } from '../../shared/api/client';
import { CLES } from '../../shared/donnees/cles';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **L'HISTORIQUE — `CDC-4` F-46, ET SA PAGINATION EST UNE RÈGLE, PAS UN CONFORT.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * « La liste se charge **par tranches**, la plus récente d'abord, **sans plafond de nombre
 * de cours**. Rien ne se tronque : **une ligne se conserve pour toujours, et un plafond
 * d'affichage rendrait inatteignable ce que le produit garde exprès.** »
 *
 * C'est exactement ce qu'un `useInfiniteQuery` fait et qu'un `useQuery` ne fait pas : il
 * **accumule** les tranches au lieu de remplacer la précédente. Charger « les cinquante
 * derniers » et s'arrêter là serait le plafond que la règle interdit.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **CE QUI N'EST PAS RÉGLABLE, ET IL N'Y A NULLE PART OÙ LE RÉGLER.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * « Une ligne par cours, **du plus récent au plus ancien, ordre unique et non réglable** »
 * (DD-33) : ce hook ne prend **aucun** paramètre de tri, et le contrat n'en porte aucun.
 * « Un tri réglable ne décide rien de plus et coûte un état de plus à chaque écran. »
 *
 * De même, **la recherche est le seul filtre**, et elle porte sur deux valeurs : « le
 * prénom d'un participant et le titre du cours, **et rien d'autre** — ce sont les deux
 * seules valeurs que la ligne porte et que la personne connaît. **Aucun filtre par date,
 * par structure ni par état.** »
 */

/** Combien de lignes par tranche. Le contrat en autorise cent au plus. */
const PAR_TRANCHE = 50;

export function useHistorique(recherche: string | undefined) {
  return useInfiniteQuery({
    queryKey: CLES.historique.liste(recherche),
    queryFn: ({ pageParam }) =>
      deballer(
        lireLHistorique({
          query: {
            combien: PAR_TRANCHE,
            ...(pageParam === null ? {} : { suite: pageParam }),
            ...(recherche === undefined ? {} : { recherche }),
          },
        }),
      ),
    initialPageParam: null as string | null,
    // **Le curseur est OPAQUE**, et le contrat l'écrit : « il ne se construit pas et ne
    // s'interprète pas côté client ». Ce hook le repasse tel quel et ne le lit jamais.
    getNextPageParam: (derniere) => derniere.suite,
    // **Les totaux arrivent avec la première tranche, et pas ailleurs.** « Le total ne
    // s'affiche nulle part ailleurs : deux emplacements pour une même valeur finiraient
    // par en montrer deux. » La route `historique/totaux` existe pour qui n'a pas besoin
    // des lignes ; cet écran a les deux, donc il ne l'appelle pas — un second appel
    // rendrait deux fois le même nombre, et à deux instants différents.
    staleTime: 30_000,
  });
}
